// MappingTable — Phase 4 Plan 04-02 Task 2 Step 2.
//
// Two-column table:
//   Your column → Maps to
//
// For high-confidence rows (confidence ≥ 0.7) — shows green "✓ {target}" chip.
// For needs_manual rows — shows a <select> dropdown with MAPPABLE_COLUMNS.
//
// Controlled-component shape (mirrors RecipientsFieldset pattern):
//   value: Record<user_header, target | "">  (empty string = unmapped/skip)
//   onChange: (header, target) => void

import type { ColumnMappingItem } from "../api/excelImport";
import { DRIVER_STATUS_COLORS } from "../lib/tokens";
import { prefersDark } from "../lib/tripStatusBadge";

export const MAPPABLE_COLUMNS = [
  "driver",
  "from",
  "to",
  "customer_email",
  "cmr",
  "external_id",
  "cargo_description",
  "skip",
] as const;

export type MappableColumn = (typeof MAPPABLE_COLUMNS)[number];

export interface MappingTableProps {
  /** Auto-detected mapping from POST /upload. */
  mapping: ColumnMappingItem[];
  /** User-edited mapping (target column per header). */
  value: Record<string, string>;
  /** Called when the user picks a target column from a dropdown. */
  onChange: (userHeader: string, target: string) => void;
}

export function MappingTable({ mapping, value, onChange }: MappingTableProps): React.JSX.Element {
  // UI-SPEC §4/§5: шрифт наследуется из Telegram-темы; фон/бордеры на
  // var(--tg-theme-*). success-chip — dark-aware DRIVER_STATUS_COLORS.online
  // (light-only #D1FAE5/#065F46 нечитаемы в dark mode).
  const dark = prefersDark();
  const chipBg = dark ? DRIVER_STATUS_COLORS.online.bgDark : DRIVER_STATUS_COLORS.online.bg;
  const chipText = dark ? DRIVER_STATUS_COLORS.online.textDark : DRIVER_STATUS_COLORS.online.text;

  return (
    <table
      data-testid="mapping-table"
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 14,
      }}
    >
      <thead>
        <tr style={{ background: "var(--tg-theme-secondary-bg-color)" }}>
          <th
            style={{
              textAlign: "left",
              padding: "8px 12px",
              borderBottom: "1px solid var(--tg-theme-hint-color)",
              fontWeight: 600,
            }}
          >
            Your column
          </th>
          <th
            style={{
              textAlign: "left",
              padding: "8px 12px",
              borderBottom: "1px solid var(--tg-theme-hint-color)",
              fontWeight: 600,
            }}
          >
            Maps to
          </th>
        </tr>
      </thead>
      <tbody>
        {mapping.map((row) => {
          const currentTarget = value[row.user_header] ?? row.mapped_to ?? "";
          const needsManual = row.needs_manual || !row.mapped_to;
          return (
            <tr
              key={row.user_header}
              data-testid={`mapping-row-${row.user_header}`}
              data-needs-manual={needsManual ? "true" : "false"}
              style={{ borderBottom: "1px solid var(--tg-theme-hint-color)" }}
            >
              <td style={{ padding: "8px 12px" }}>{row.user_header}</td>
              <td style={{ padding: "8px 12px" }}>
                {needsManual ? (
                  <select
                    data-testid={`mapping-select-${row.user_header}`}
                    value={currentTarget || ""}
                    onChange={(e) => onChange(row.user_header, e.target.value)}
                    style={{
                      padding: "6px 8px",
                      fontSize: 14,
                      minWidth: 160,
                    }}
                  >
                    <option value="">— Select target —</option>
                    {MAPPABLE_COLUMNS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span
                    data-testid={`mapping-chip-${row.user_header}`}
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 12,
                      background: chipBg,
                      color: chipText,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    ✓ {currentTarget}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
